import React, { useState, useEffect, useRef, ReactNode } from 'react';
import cls from '@soluto-design/styles/cls';

export interface FormProps {
    id?: string;
    src: string;
    onReady?: () => void;
    onSubmit?: (event?: Record<string, unknown>) => void;
    className?: string;
    theme?: string;
    loading?: ReactNode;
}

declare global {
    interface Window {
        dataLayer: any[];
    }
}

function getId() {
    return Math.random().toString(36).substr(2, 16);
}

const ALLOWED_ORIGINS = [
    'https://to.ratedpower.com',
    'https://to.full2.ratedpower.com',
];

const Form: React.FC<FormProps> = ({
    id,
    onReady,
    onSubmit,
    src,
    className,
    theme,
    loading,
}) => {
    const [isReady, setIsReady] = useState(false);
    const [iframeSrc, setIframeSrc] = useState<string | null>(null);
    const [height, setHeight] = useState<string | null>(null);

    const idRef = useRef(getId());
    const wrapperRef = useRef<HTMLDivElement>(null);
    const iframeRef = useRef<HTMLIFrameElement>(null);

    useEffect(() => {
        // get utm params and update iframe src
        if (typeof window === 'undefined' || !wrapperRef?.current) return;

        let interval: any = null;
        let maxTimes: number = 15;
        const url = new URL(src);

        let thisTheme = theme;

        if (!thisTheme) {
            const closestDataTheme =
                wrapperRef.current?.closest('[data-theme]');
            if (closestDataTheme) {
                thisTheme = (closestDataTheme as HTMLElement).dataset.theme;
            }
        }

        if (thisTheme) {
            url.searchParams.set('theme', thisTheme);
        }

        interval = setInterval(() => {
            maxTimes--;

            if (!maxTimes) {
                setIframeSrc(url.toString());
                clearInterval(interval);
                return;
            }

            if (!(window as any).google_tag_manager) return;

            const GTM_ID = Object.keys((window as any).google_tag_manager).find(
                (k) => k.startsWith('GTM-'),
            );

            if (!GTM_ID) return;

            const urlParams = (window as any).google_tag_manager[
                GTM_ID
            ].dataLayer?.get('urlParams');

            if (!urlParams) return;

            Object.keys(urlParams).forEach((key) => {
                url.searchParams.set(key, urlParams[key]);
            });

            setIframeSrc(url.toString());
            clearInterval(interval);
        }, 150);

        return () => {
            clearInterval(interval);
        };
    }, [src, wrapperRef, theme]);

    useEffect(() => {
        function getHeight() {
            const iframe = iframeRef.current;

            if (!iframe?.contentWindow) return;

            iframe?.contentWindow?.postMessage(
                `pardot-form-get-height-${idRef?.current}`,
                '*',
            );
        }

        if (isReady) {
            getHeight();

            if (typeof onReady === 'function') {
                onReady();
            }

            window.addEventListener('resize', getHeight);
        }

        return () => {
            window.removeEventListener('resize', getHeight);
        };
    }, [isReady, onReady]);

    useEffect(() => {
        if (!isReady || typeof window === 'undefined') return;

        const handleMessage = (event: any) => {
            if (!ALLOWED_ORIGINS.includes(event?.origin)) {
                return;
            }

            if (
                event?.data?.id &&
                event?.data?.height &&
                idRef.current === event.data.id
            ) {
                setHeight(event.data.height);
            } else if (event?.data?.event) {
                const data = event.data;

                const dataLayer = window?.dataLayer ?? (window.dataLayer = []);
                dataLayer.push(data);

                if (
                    data.event === 'pardot-form-success' &&
                    typeof onSubmit === 'function'
                ) {
                    onSubmit(data);
                }
            }
        };

        window.addEventListener('message', handleMessage);

        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [isReady, onSubmit]);

    const isLoading = !isReady && iframeSrc;

    const style: React.CSSProperties = height
        ? ({
              '--iframe-height': `${height}px`,
          } as React.CSSProperties)
        : {};

    return (
        <div ref={wrapperRef} className={cls('mx-[-2px]', className)}>
            {isLoading
                ? loading || (
                      <span className={cls('text-text-secondary')}>
                          Loading...
                      </span>
                  )
                : null}
            {iframeSrc && (
                <iframe
                    id={id}
                    ref={iframeRef}
                    src={iframeSrc}
                    width="100%"
                    height="100%"
                    style={style}
                    className={cls(
                        'w-full h-full border-none max-h-0 !overflow-hidden',
                        isReady && 'max-h-[9999px] !h-[var(--iframe-height)]',
                    )}
                    onLoad={() => {
                        setIsReady(true);
                    }}
                />
            )}
        </div>
    );
};

export default Form;
